import {
  CONDITIONAL_BOTTOM_RATIO,
  CONDITIONAL_TOP_RATIO,
  NODE_HEIGHT,
  NODE_WIDTH,
  WORLD_ORIGIN,
} from './constants'
import { actionOutputPorts } from '../../../../../shared/flow-core/src/execution'
import type { FlowNode, Point } from './types'

const nPorts = (n: number, prefix: string): string[] =>
  Array.from({ length: Math.max(1, n) }, (_, i) => `${prefix}${i}`)

export const outputPorts = (node: FlowNode): string[] => {
  if (node.type === 'condition') return ['true', 'else']
  if (node.type === 'end') return []
  if (node.type === 'macro-start') return nPorts(node.data.inputCount ?? 1, 'out-')
  if (node.type === 'macro-end') return nPorts(node.data.outputCount ?? 1, 'out-')
  if (node.type === 'macro-call') return nPorts(node.data.outputCount ?? 1, 'out-')
  if (node.type === 'action') return actionOutputPorts(node.data) ?? ['next']
  return ['next']
}

export const inputPorts = (node: FlowNode): string[] => {
  if (node.type === 'start') return []
  if (node.type === 'condition' || node.type === 'end') return ['in-a', 'in-b']
  if (node.type === 'macro-start') return nPorts(node.data.inputCount ?? 1, 'in-')
  if (node.type === 'macro-end') return nPorts(node.data.outputCount ?? 1, 'in-')
  if (node.type === 'macro-call') return nPorts(node.data.inputCount ?? 1, 'in-')
  return ['in']
}

/** Canvas position (inside the world element) where an edge enters a node. */
export const inputPoint = (node: FlowNode, port: string): Point => {
  const ports = inputPorts(node)
  const index = Math.max(0, ports.indexOf(port))
  const ratio = ports.length > 1 ? (index === 0 ? 0.38 : 0.62) : 0.5
  return {
    x: node.x + WORLD_ORIGIN,
    y: node.y + WORLD_ORIGIN + NODE_HEIGHT * ratio,
  }
}

/** Canvas position (inside the world element) where an edge leaves a node. */
export const outputPoint = (node: FlowNode, port: string): Point => {
  if (node.type === 'condition') {
    const ratio = port === 'true' ? CONDITIONAL_TOP_RATIO : CONDITIONAL_BOTTOM_RATIO
    return {
      x: node.x + WORLD_ORIGIN + NODE_WIDTH,
      y: node.y + WORLD_ORIGIN + NODE_HEIGHT * ratio,
    }
  }
  // Several outputs are spread evenly, matching the handle positions in FlowNodeCard.
  const ports = outputPorts(node)
  const index = Math.max(0, ports.indexOf(port))
  return {
    x: node.x + WORLD_ORIGIN + NODE_WIDTH,
    y: node.y + WORLD_ORIGIN + (NODE_HEIGHT * (index + 1)) / (ports.length + 1),
  }
}

/** Macro nodes colour their ports by index so matching inputs/outputs line up visually. */
export const hasIndexedPorts = (node: FlowNode) =>
  node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call'
